/** @jsx jsx */
import { Fragment } from 'preact';
import { useRef } from 'preact/hooks';
import { jsx, css } from '@emotion/react';
import classnames from 'classnames';
import { observer } from 'mobx-react-lite';
import deepmerge from 'deepmerge';
import SwiperCore, { Pagination, Navigation, A11y } from 'swiper/core';
import { Icon } from '../../Atoms/Icon/Icon';
import { Swiper, SwiperSlide } from 'swiper/react';
import { defined } from '../../../utilities';
import { useTheme, CacheProvider } from '../../../providers';
import { useDisplaySettings } from '../../../hooks/useDisplaySettings';
const CSS = {
    carousel: ({ theme, vertical }) => css({
        display: 'flex',
        maxWidth: '100%',
        maxHeight: vertical ? '100%' : undefined,
        margin: 0,
        padding: 0,
        overflow: 'hidden',
        '.swiper-notification': {
            position: 'absolute',
            left: '100000000000000px',
        },
        '&.ss__carousel-vertical': {
            flexDirection: 'column',
            '.swiper-slide': {
                /* Center slides vertically */
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
            },
            '.swiper-container': {
                flexDirection: 'row',
            },
            '.swiper-pagination': {
                width: 'auto',
                order: 0,
                flexDirection: 'column',
                margin: 0,
                padding: '10px',
            },
            '.swiper-pagination-bullet': {
                margin: '4px',
            },
        },
        '.swiper-pagination-bullet-active': {
            background: theme?.colors?.primary || 'inherit',
        },
        '.ss__carousel__next-wrapper, .ss__carousel__prev-wrapper': {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            '&.ss__carousel__next-wrapper--hidden, &.ss__carousel__prev-wrapper--hidden': {
                display: 'none',
            },
        },
        '.ss__carousel__next, .ss__carousel__prev': {
            padding: '5px',
            cursor: 'pointer',
            lineHeight: 0,
            '&.swiper-button-disabled': {
                opacity: '0.3',
                cursor: 'default',
            },
        },
        '.swiper-container': {
            display: 'flex',
            flexDirection: 'column',
            marginLeft: 'auto',
            marginRight: 'auto',
            position: 'relative',
            overflow: 'hidden',
            listStyle: 'none',
            padding: 0,
            zIndex: '1',
        },
        '.swiper-container-vertical': {
            '.swiper-wrapper': {
                flexDirection: 'column',
            },
        },
        '.swiper-wrapper': {
            order: 0,
            position: 'relative',
            width: '100%',
            height: '100%',
            zIndex: '1',
            display: 'flex',
            transitionProperty: 'transform',
            boxSizing: 'content-box',
        },
        '.swiper-slide': {
            flexShrink: 0,
            width: '100%',
            height: '100%',
            position: 'relative',
            transitionProperty: 'transform',
        },
        '.swiper-pagination': {
            display: 'flex',
            justifyContent: 'center',
            marginTop: '10px',
            width: '100%',
            order: 1,
            transition: '.3s opacity',
        },
        '.swiper-pagination-bullet': {
            width: '8px',
            height: '8px',
            display: 'inline-block',
            borderRadius: '50%',
            background: '#000',
            opacity: '.2',
            cursor: 'pointer',
            margin: '0 4px',
            '&.swiper-pagination-bullet-active': {
                opacity: '0.8',
                background: theme?.colors?.primary || '#000',
            },
        },
        '.swiper-container-pointer-events': {
            touchAction: 'pan-y',
            '&.swiper-container-vertical': {
                touchAction: 'pan-x',
            },
        },
        '.swiper-slide-invisible-blank': {
            visibility: 'hidden',
        },
    }),
};
export const defaultCarouselBreakpoints = {
    0: {
        slidesPerView: 1,
        slidesPerGroup: 1,
        spaceBetween: 0,
    },
    480: {
        slidesPerView: 2,
        slidesPerGroup: 2,
        spaceBetween: 10,
    },
    768: {
        slidesPerView: 3,
        slidesPerGroup: 3,
        spaceBetween: 10,
    },
    1024: {
        slidesPerView: 4,
        slidesPerGroup: 4,
        spaceBetween: 10,
    },
    1200: {
        slidesPerView: 5,
        slidesPerGroup: 5,
        spaceBetween: 10,
    },
};
export const defaultVerticalCarouselBreakpoints = {
    0: {
        slidesPerView: 1,
        slidesPerGroup: 1,
        spaceBetween: 0,
    },
};
export const Carousel = observer((properties) => {
    const globalTheme = useTheme();
    const theme = { ...globalTheme, ...properties.theme };
    let props = {
        // default props
        breakpoints: properties.vertical
            ? JSON.parse(JSON.stringify(defaultVerticalCarouselBreakpoints))
            : JSON.parse(JSON.stringify(defaultCarouselBreakpoints)),
        pagination: false,
        loop: true,
        autoAdjustSlides: true,
        // global theme
        ...globalTheme?.components?.carousel,
        //props
        ...properties,
        ...properties.theme?.components?.carousel,
    };
    const displaySettings = useDisplaySettings(props.breakpoints);
    if (displaySettings && Object.keys(displaySettings).length) {
        const theme = deepmerge(props?.theme || {}, displaySettings?.theme || {}, { arrayMerge: (destinationArray, sourceArray) => sourceArray });
        if (props.autoAdjustSlides && props.children.length < displaySettings.slidesPerView) {
            displaySettings.slidesPerView = props.children.length;
            displaySettings.slidesPerGroup = props.children.length;
            displaySettings.loop = false;
        }
        props = {
            ...props,
            ...displaySettings,
            theme,
        };
    }
    const { children, loop, nextButton, prevButton, hideButtons, vertical, onInit, onNextButtonClick, onPrevButtonClick, onClick, disableStyles, style, modules, className, ...additionalProps } = props;
    let pagination = props.pagination;
    const subProps = {
        icon: {
            // default props
            className: 'ss__carousel__icon',
            // global theme
            ...globalTheme?.components?.icon,
            // inherited props
            ...defined({
                disableStyles,
            }),
            // component theme overrides
            theme: props.theme,
        },
    };
    const swiperModulesUnfiltered = modules ? [Navigation, Pagination, A11y].concat(modules) : [Navigation, Pagination, A11y];
    //remove any duplicates, in case user passes in Navigation or Pagination
    const swiperModules = swiperModulesUnfiltered.filter((module, pos) => swiperModulesUnfiltered.indexOf(module) === pos);
    SwiperCore.use(swiperModules);
    const navigationPrevRef = useRef(null);
    const navigationNextRef = useRef(null);
    const rootComponentRef = useRef(null);
    const styling = {};
    if (!disableStyles) {
        styling.css = [CSS.carousel({ theme, vertical }), style];
    }
    else if (style) {
        styling.css = [style];
    }
    if (pagination) {
        if (typeof pagination == 'object') {
            pagination = {
                clickable: true,
                ...pagination,
            };
        }
        else {
            pagination = {
                clickable: true,
            };
        }
    }
    return children?.length ? (jsx(CacheProvider, null,
        jsx("div", { ref: rootComponentRef, ...styling, className: classnames('ss__carousel', vertical ? 'ss__carousel-vertical' : '', className) },
            jsx("div", { className: classnames('ss__carousel__prev-wrapper', { 'ss__carousel__prev-wrapper--hidden': hideButtons }) },
                jsx("div", { className: "ss__carousel__prev", ref: navigationPrevRef, onClick: onPrevButtonClick && ((e) => onPrevButtonClick(e)) }, prevButton || jsx(Icon, { icon: vertical ? 'angle-up' : 'angle-left', ...subProps.icon }))),
            jsx(Swiper, { centerInsufficientSlides: true, onInit: (swiper) => {
                    //@ts-ignore : someone should refactor this
                    swiper.params.navigation.prevEl = navigationPrevRef.current ? navigationPrevRef.current : undefined;
                    //@ts-ignore : someone should refactor this
                    swiper.params.navigation.nextEl = navigationNextRef.current ? navigationNextRef.current : undefined;
                    if (onInit) {
                        onInit(swiper);
                    }
                }, onClick: (swiper, e) => {
                    onClick && onClick(swiper, e);
                }, direction: vertical ? 'vertical' : 'horizontal', loop: loop, threshold: 7, ...additionalProps, ...displaySettings, pagination: pagination }, children.map((child) => {
                return jsx(SwiperSlide, null, child);
            })),
            jsx("div", { className: classnames('ss__carousel__next-wrapper', { 'ss__carousel__next-wrapper--hidden': hideButtons }) },
                jsx("div", { className: "ss__carousel__next", ref: navigationNextRef, onClick: onNextButtonClick && ((e) => onNextButtonClick(e)) }, nextButton || jsx(Icon, { icon: vertical ? 'angle-down' : 'angle-right', ...subProps.icon })))))) : (jsx(Fragment, null));
});
